import Section from "../Section";
import MobileMenu from '../global/MobileMenu';
import Menu from '../global/Menu';
import { CART_UPDATE_EVENT } from '../ThemeBase';
import { initiateFadeInUpAnimation, removeFadeInUpAnimation } from '../global/fadeInUpAnimation';

export default class Header extends Section {
  /**
   * @param {import('../ThemeBase').default} theme
   * @param {HTMLElement} element
   * @param {boolean} pageLoad
   */
  constructor(theme, element, pageLoad) {
    super(theme, element, pageLoad);

    this.element = element;

    // Define named functions for event handlers
    this.handleCartButtonClick = this.onCartButtonClick.bind(this);
    this.handleMobileMenuButtonClick = this.onMobileMenuButtonClick.bind(this);
    this.handleNavDrawerOverlayClick = this.onNavDrawerOverlayClick.bind(this);
    this.handleCartUpdate = this.onCartUpdate.bind(this);
    this.handleOffersLinkClick = this.onOffersLinkClick.bind(this);
    Array.prototype.forEach.call(
      this.element.querySelectorAll('.js-drawer-open-right-link'),
      (el) => el.addEventListener('click', this.handleCartButtonClick),
    );
    this.element.querySelector('.js-drawer-open-left-link').addEventListener('click', this.handleMobileMenuButtonClick);
    this.mobileMenu = new MobileMenu(theme, this.element.querySelector('.drawer--left'));

    // Offers drawer
    const offersLinks = this.element.querySelectorAll('.js-drawer-open-offers-link');
    if (offersLinks.length > 0) {
      offersLinks.forEach(el => el.addEventListener('click', this.handleOffersLinkClick));

      // If the icon setting is changed, need to inject the new icon in the drawer
      if (window.Shopify.designMode) {
        window.eventBus.emit('injectIcon:offers:drawer');
      }
    } else if (window.Shopify.designMode) {
      // If no offers icon and the drawer is open in the editor, close it
      window.eventBus.emit('close:offers:drawer');
    }

    document.documentElement.addEventListener(CART_UPDATE_EVENT, this.handleCartUpdate);

    this.handleStickyHeaderVars();

    // Close mobile menu on overlay click
    this.element.querySelector('#NavDrawerOverlay').addEventListener('click', this.handleNavDrawerOverlayClick);

    this.loadAccessibleMenu();
    if (this.element.dataset.headerSticky === 'true' && (this.element.dataset.inspectorActive == 'false' || !this.element.dataset.inspectorActive)) {
      this.loadStickyHeader();
    } else {
      this.loadHeaderObserver();
    }
    this.loadSearchDrawer();
    this.menu = new Menu(this, element);
    this.handleExternalLinks();

    // Initialize ResizeObserver
    this.initResizeObserver();

    // Fallback: Add window resize listener in case ResizeObserver is not supported
    if (!('ResizeObserver' in window)) {
      window.addEventListener('resize', this.onWindowResize);
    }
  }

  initResizeObserver() {
    if ('ResizeObserver' in window) {
      // Create a new ResizeObserver instance
      this.resizeObserver = new ResizeObserver(entries => {
        for (let entry of entries) {
          if (entry.target.classList.contains('mobile-nav-bar-wrapper') || entry.target.classList.contains('site-header')) {
            this.updateHeaderHeightVariables();
          }
        }
      });

      // Select the elements to observe
      const mobileNavBar = this.element.querySelector('.mobile-nav-bar-wrapper');
      const siteHeader = this.element.querySelector('.site-header');

      // Observe the elements if they exist
      if (mobileNavBar) this.resizeObserver.observe(mobileNavBar);
      if (siteHeader) this.resizeObserver.observe(siteHeader);
    }
  }

  updateHeaderHeightVariables() {
    this.handleStickyHeaderVars();
  }

  onNavDrawerOverlayClick() {
    this.theme.toggleLeftDrawer(false);
  }

  handleStickyHeaderVars = () => {
    const isStickyHeader = this.element.dataset.headerSticky === 'true';
    if (isStickyHeader) {
      this.setStickyHeaderVar('.mobile-nav-bar-wrapper', '--sticky-header-mobile-height');
      this.setStickyHeaderVar('.site-header', '--sticky-header-desktop-height');
    } else {
      this.resetStickyHeaderVar('--sticky-header-mobile-height');
      this.resetStickyHeaderVar('--sticky-header-desktop-height');
    }

    this.setStickyHeaderVar('.mobile-nav-bar-wrapper', '--header-height-mobile');
    this.setStickyHeaderVar('.site-header', '--header-height-desktop');
  }
  
  setStickyHeaderVar = (selector, varName) => {
    const header = this.element.querySelector(selector);
    const headerHeight = header.offsetHeight;
    document.documentElement.style.setProperty(varName, `${headerHeight}px`);
  }
  
  resetStickyHeaderVar = (varName) => {
    document.documentElement.style.setProperty(varName, `0px`);
  }
  
  onCartUpdate = (e) => {
    Array.prototype.forEach.call(
      document.querySelectorAll('.cart-item-count-header--quantity'),
      (el) => {
        el.classList.add('hide');

        const cartHasItems = e.detail.cart.item_count > 0;
        el.textContent = e.detail.cart.item_count;

        if (cartHasItems) el.classList.remove('hide');
      },
    );
  }

  onCartButtonClick = (e) => {
    e.preventDefault();
    window.eventBus.emit('open:cart:drawer', { scrollToTop: false });
  }

  onOffersLinkClick = (e) => {
    e.preventDefault();
    window.eventBus.emit('open:offers:drawer');
  }

  onMobileMenuButtonClick = (e) => {
    e.preventDefault();
    this.mobileMenu.toggle();
  }

  loadSearchDrawer = () => {
    document.querySelectorAll('.search-button').forEach(item => {
      item.addEventListener('click', this.onSearch);
    });
  }

  onSearch = (e) => {
    e.preventDefault();
    this.theme.toggleRightDrawer('search', true);
  }

  loadAccessibleMenu = () => {
    const siteNav = this.element.querySelector('.site-nav');
    if (siteNav) {
      siteNav.classList.add('js');
    }

    this.element.querySelectorAll('.site-nav li a').forEach(anchor => {
      anchor.addEventListener('focus', function() {
        const parent = this.parentElement;
        if (parent) {
          const ul = parent.querySelector('ul');
          if (ul) {
            ul.style.display = 'block';
          }
        }
      });
    });

    this.element.querySelectorAll('.site-nav li li a').forEach(anchor => {
      anchor.removeEventListener('focus', () => {});
    });

    this.element.querySelectorAll('.mobile-nav__link').forEach(link => {
      link.addEventListener('click', (e) => {
        const a = /** @type {HTMLAnchorElement} */ (e.currentTarget);
        if (
          a.href &&
          a.href.split('#')[0] === window.location.href.split('#')[0]
        ) {
          // Anchor on same page - just close the drawer
          this.theme.toggleLeftDrawer(false);
        }
      });
    });

    this.tabindexMobileNav();
  };

  tabindexMobileNav = () => {
    // Mobile menu should not be keyboard-navigable while viewing on desktop.
    const navDrawerLinks = this.element.querySelectorAll('#NavDrawer a, #NavDrawer button');
    if(window.innerWidth > 767) {
      navDrawerLinks.forEach(link => link.setAttribute('tabindex', '-1'));
    }
    else {
      navDrawerLinks.forEach(link => link.removeAttribute('tabindex'));
    }
  }

  onWindowResize = () => {
    this.tabindexMobileNav();
    this.handleStickyHeaderVars();
  }

  // Sticky page header
  loadStickyHeader = () => {

    if ('IntersectionObserver' in window) {
      // We need to apply sticky to child of element that is being scrolled, in
      // this case it's the whole `shopify-section`
      const headerWrapper = this.element.closest('.shopify-section.header-section');
      headerWrapper.classList.add('sticky-header');
  
      const observer = new IntersectionObserver(
        ([e]) => {
          if (e.intersectionRatio < 1) {
            e.target.classList.add('sticked');
            // Dispatch a custom event when the header becomes 'sticked'
            const stickedEvent = new CustomEvent('headerSticked', { detail: e.target });
            document.dispatchEvent(stickedEvent);
          } else {
            e.target.classList.remove('sticked');
            // Dispatch a custom event when the header becomes 'unstuck'
            const unstuckEvent = new CustomEvent('headerUnstuck', { detail: e.target });
            document.dispatchEvent(unstuckEvent);
          }
        },
        {
          threshold: [1],
        },
      );
  
      observer.observe(headerWrapper);
    }
  }

  loadHeaderObserver = () => {
    if ('IntersectionObserver' in window) {
      const headerWrapper = this.element.closest('.shopify-section.header-section');
  
      const observer = new IntersectionObserver(
        ([e]) => {
          if (e.intersectionRatio > 0) {
            e.target.classList.remove('is-off-screen');
          } else {
            e.target.classList.add('is-off-screen');
          }
        },
        {
          threshold: [0],
        },
      );
  
      observer.observe(headerWrapper);
    }
  }

  onBlockSelect = ({ element }) => {
    // Prevent menu reopening on deselect due to editor event bug where select event is fired on deselect after editing a text input setting
    if (document.body.hasAttribute('data-mega-menu-deselected')) return;

    // Open mega menu and make overlay force-hovered
    if (element.dataset.megaMenuBlockId) {
      const megaMenu = this.element.querySelector(`[data-mega-menu-block-id='${element.dataset.megaMenuBlockId}']`);
      if (megaMenu) {
        const li = megaMenu.closest('li.mega-menu');
        if (li) {
          li.classList.add('force-open');
        }
      }
      const overlayHeader = this.element.querySelector('.overlay-header');
      if (overlayHeader) {
        overlayHeader.classList.add('force-hover');
      }

      const megaMenuChildrenArray = Array.from(this.element.querySelectorAll('li.mega-menu.force-open .mega-menu-block .dropdown-submenu'));
      initiateFadeInUpAnimation(megaMenuChildrenArray); // Initiate mega menu animation
    }
  }

  onBlockDeselect = () => {
    // Needed to prevent menu reopening on deselect due to editor event bug where select event is fired on deselect after editing a text input setting
    document.body.setAttribute('data-mega-menu-deselected', 'true');
    setTimeout(() => {
      document.body.removeAttribute('data-mega-menu-deselected');
    }, 500);

    // Close mega menu and make overlay non-hovered
    this.element.querySelectorAll('li.mega-menu.force-open').forEach(li => li.classList.remove('force-open'));
    this.element.querySelectorAll('.overlay-header').forEach(header => header.classList.remove('force-hover'));

    const megaMenuChildrenArray = Array.from(this.element.querySelectorAll('li.mega-menu .mega-menu-block .dropdown-submenu'));
    removeFadeInUpAnimation(megaMenuChildrenArray); // Remove mega menu animation
  }

  handleExternalLinks = () => {
    const links = this.element.querySelectorAll('a');
    links.forEach(link => {
      if (link.hostname !== window.location.hostname) {
        link.target = '_blank';
        link.rel = 'noreferrer noopener';
      }
    });
  }

  disconnectedCallback() {
    if (!('ResizeObserver' in window)) {
      window.removeEventListener('resize', this.onWindowResize);
    }
    
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }
}
